import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { getStoreScheduleSummaryApi } from "../api/scheduleApi";
import { duration, getPresenceApi, hhmm, Presence, PRESENCE_BADGE, PRESENCE_LABELS, PresenceRow, timeOf } from "../api/timekeepingApi";
import PunchPhotoModal from "../components/PunchPhotoModal";
import { DP_PAGE, DP_TIMESHEET_PAGE, DP_TIMESHEETS_PAGE } from "../routes";
import "./Dp.css";
import "./DpEco.css";
import "./Timekeeping.css";

const todayIso = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });
const nameOf = (r: PresenceRow) => r.employee.socialName || r.employee.fullName;
const shiftOf = (r: PresenceRow) =>
  r.expected?.startMinute != null && r.expected.endMinute != null
    ? `${hhmm(r.expected.startMinute)}–${hhmm(r.expected.endMinute)}`
    : r.expected
      ? "Sem horário (escala padrão)"
      : "—";

/**
 * Presença do dia por loja: quem está previsto pela escala, quem chegou, quem está
 * no intervalo, quem saiu e quem faltou. "Faltou" só aparece depois do início do
 * turno + tolerância + 1 hora. Sem atualização automática: botão "Atualizar".
 */
export default function PresencePage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_TIMESHEETS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [params, setParams] = useSearchParams();
  const storeId = params.get("loja") ?? "";
  const date = params.get("data") ?? todayIso();

  const [stores, setStores] = useState<Array<{ storeId: string; storeName: string }>>([]);
  const [data, setData] = useState<Presence | null>(null);
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState<{ row: PresenceRow; punch: PresenceRow["punches"][number] } | null>(null);

  const setFilter = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!token) return;
    getStoreScheduleSummaryApi(token)
      .then((s) => setStores(s.stores.map(({ storeId: id, storeName }) => ({ storeId: id, storeName }))))
      .catch(() => setStores([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token || !storeId) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      setData(await getPresenceApi(token, storeId, date));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar a presença do dia.");
    } finally {
      setLoading(false);
    }
  }, [token, storeId, date]);

  useEffect(() => {
    load();
  }, [load]);

  if (!token) return null;
  const t = data?.totals;
  const [y, m] = date.split("-").map(Number);
  const openSheet = (r: PresenceRow) => navigate(DP_TIMESHEET_PAGE(r.contractId, y, m));

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Ponto", onClick: () => navigate(DP_TIMESHEETS_PAGE()) },
              { label: "Presença do dia" },
            ]}
            title="Presença do dia"
            subtitle="Quem está previsto pela escala, quem chegou, quem está no intervalo, quem saiu e quem faltou."
            onBack={goBack}
            backLabel="Voltar ao ponto"
            actions={
              <button type="button" className="ecoBtn" onClick={load} disabled={loading || !storeId}>
                {loading ? "Atualizando..." : "Atualizar"}
              </button>
            }
          />

          <div className="dpControls">
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="prDate">Dia</label>
              <input id="prDate" type="date" className="dpInput" value={date} onChange={(e) => setFilter({ data: e.target.value === todayIso() ? "" : e.target.value })} />
            </div>
            <div className="dpFormGroup" style={{ margin: 0 }}>
              <label htmlFor="prStore">Loja</label>
              <select id="prStore" className="dpSelect" value={storeId} onChange={(e) => setFilter({ loja: e.target.value })}>
                <option value="">Escolha a loja</option>
                {stores.map((s) => (
                  <option key={s.storeId} value={s.storeId}>
                    {s.storeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!storeId ? <p className="dpNotice">Escolha a loja para ver a presença do dia.</p> : null}
          {data && !data.enabled ? <p className="dpNotice dpNotice--warning">O ponto eletrônico não estava ligado neste dia.</p> : null}

          {t ? (
            <div className="dpTkTotals">
              <div className="dpTkTotal">
                <span>Previstos</span>
                <strong>{t.expected}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Presentes</span>
                <strong>{t.PRESENTE}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Em intervalo</span>
                <strong>{t.EM_INTERVALO}</strong>
              </div>
              <div className="dpTkTotal">
                <span>Saíram</span>
                <strong>{t.SAIU}</strong>
              </div>
              <div className={`dpTkTotal ${t.FALTOU ? "dpTkTotal--bad" : ""}`}>
                <span>Faltaram</span>
                <strong>{t.FALTOU}</strong>
              </div>
              <div className={`dpTkTotal ${t.late ? "dpTkTotal--warn" : ""}`}>
                <span>Chegaram atrasados</span>
                <strong>{t.late}</strong>
              </div>
              <div className={`dpTkTotal ${t.outsideStore ? "dpTkTotal--warn" : ""}`}>
                <span>Marcação fora da loja</span>
                <strong>{t.outsideStore}</strong>
              </div>
            </div>
          ) : null}

          {data ? (
            <>
              <div className="dpTableWrapper dpTkTableDesktop">
                <table className="dpTable">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>Colaborador</th>
                      <th>Turno previsto</th>
                      <th>Marcações</th>
                      <th>Atraso</th>
                      <th>Situação</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {!data.rows.length ? (
                      <tr>
                        <td colSpan={6} className="dpEmpty">
                          Nenhum colaborador lotado nesta loja.
                        </td>
                      </tr>
                    ) : (
                      data.rows.map((r) => (
                        <tr key={r.contractId}>
                          <td>
                            <strong>{nameOf(r)}</strong>
                          </td>
                          <td>{shiftOf(r)}</td>
                          <td>
                            <Marks r={r} onPhoto={(punch) => setPhoto({ row: r, punch })} />
                          </td>
                          <td className="dpTkNum">{r.lateMinutes ? duration(r.lateMinutes) : "—"}</td>
                          <td>
                            <span className={`dpBadge ${PRESENCE_BADGE[r.state]}`}>{PRESENCE_LABELS[r.state]}</span>
                          </td>
                          <td>
                            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openSheet(r)}>
                              Espelho
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="dpTkCards">
                {data.rows.map((r) => (
                  <article key={r.contractId} className="dpTkCard">
                    <div className="dpTkCard__head">
                      <span className="dpTkPerson">
                        <strong>{nameOf(r)}</strong>
                        <small>{shiftOf(r)}</small>
                      </span>
                      <span className={`dpBadge ${PRESENCE_BADGE[r.state]}`}>{PRESENCE_LABELS[r.state]}</span>
                    </div>
                    <Marks r={r} onPhoto={(punch) => setPhoto({ row: r, punch })} />
                    {r.lateMinutes ? <div className="dpMuted">Atraso de {duration(r.lateMinutes)}</div> : null}
                    <div className="dpTkActions">
                      <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openSheet(r)}>
                        Abrir espelho
                      </button>
                    </div>
                  </article>
                ))}
                {!data.rows.length ? <div className="dpEmpty">Nenhum colaborador lotado nesta loja.</div> : null}
              </div>
            </>
          ) : null}
        </div>
      </main>

      {photo ? (
        <PunchPhotoModal
          token={token}
          punchId={photo.punch.id}
          title={`${nameOf(photo.row)} · ${timeOf(photo.punch.at)}`}
          details={[`${data?.store.nomeFantasia ?? ""}${photo.punch.outsideStore ? " · marcação longe da loja" : ""}`].filter(Boolean)}
          onClose={() => setPhoto(null)}
        />
      ) : null}
    </div>
  );
}

/** Marcações do dia; a que tem selfie vira botão que abre a foto. */
function Marks({ r, onPhoto }: { r: PresenceRow; onPhoto: (punch: PresenceRow["punches"][number]) => void }) {
  if (!r.punches.length) return <span className="dpMuted">—</span>;
  return (
    <div className="dpTkIssues">
      {r.punches.map((p) => {
        const className = `dpTkFlag ${p.outsideStore ? "" : "dpTkFlag--info"}`;
        const title = p.outsideStore ? "Longe da loja" : undefined;
        return p.hasPhoto ? (
          <button key={p.at} type="button" className={`${className} dpTkFlag--button`} title={title} onClick={() => onPhoto(p)}>
            {timeOf(p.at)} · foto
          </button>
        ) : (
          <span key={p.at} className={className} title={title}>
            {timeOf(p.at)}
          </span>
        );
      })}
    </div>
  );
}
